import type { FC, ReactNode } from 'react'

import Link from 'next/link'

import { event, footerColumns, venueAddress } from '@/constants/metric'
import { NewsletterForm } from '@/components/generals/newsletter-form'
import { SwapLabel } from '@/components/generals/swap-label'

const year = 2026

export const Footer: FC = (): ReactNode => {
    return (
        <footer id='footer' className='border-t border-line'>
            <div className='px-0 py-14 tab:px-[60px] tab:py-20'>
                <div className='fw-container grid gap-12 desk:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] desk:gap-16'>
                    <div data-reveal='up' className='flex flex-col gap-5'>
                        <h2 className='font-sans text-[26px] font-medium leading-[1.2em] text-ink'>
                            Stay in the loop
                        </h2>

                        <p className='max-w-[420px] font-body text-[17px] leading-[1.3em] text-ink-soft'>
                            Get updates on speakers, sessions, and early ticket releases.
                        </p>

                        <NewsletterForm />
                    </div>

                    <div className='grid gap-10 sm:grid-cols-3'>
                        {footerColumns.map((column, index) => (
                            <div
                                key={column.title}
                                data-reveal='up'
                                data-reveal-delay={0.06 * (index + 1)}
                                className='flex flex-col gap-4'
                            >
                                <h3 className='font-sans text-[14px] font-medium uppercase leading-[1em] tracking-[0.02em] text-ink-mute'>
                                    {column.title}
                                </h3>

                                <ul className='flex flex-col gap-3'>
                                    {column.links.map((link) => (
                                        <li key={`${column.title}-${link.href}-${link.label}`}>
                                            <Link
                                                href={link.href}
                                                className='fw-swap-host block font-body text-[16px] leading-[1.4em] text-ink-soft transition-colors duration-300 hover:text-ink'
                                            >
                                                <SwapLabel>
                                                    {link.label}
                                                </SwapLabel>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}

                        <div data-reveal='up' data-reveal-delay='0.18' className='flex flex-col gap-4'>
                            <h3 className='font-sans text-[14px] font-medium uppercase leading-[1em] tracking-[0.02em] text-ink-mute'>
                                Location
                            </h3>

                            <address className='not-italic'>
                                <ul className='flex flex-col gap-1'>
                                    {venueAddress.map((line) => (
                                        <li key={line} className='font-body text-[16px] leading-[1.4em] text-ink-soft'>
                                            {line}
                                        </li>
                                    ))}
                                </ul>
                            </address>

                            <a
                                href={event.mapsUrl}
                                target='_blank'
                                rel='noreferrer noopener'
                                className='fw-swap-host inline-block w-fit font-body text-[16px] leading-[1.4em] text-ink underline underline-offset-4 transition-colors duration-300 hover:text-white'
                            >
                                <SwapLabel>
                                    Directions
                                </SwapLabel>
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <div className='border-t border-line px-0 py-6 tab:px-[60px]'>
                <div className='fw-container flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center'>
                    <p className='font-body text-[14px] leading-[1.4em] text-ink-mute'>
                        © {year} {event.name} {event.edition}. All rights reserved.
                    </p>

                    <p className='font-sans text-[13px] font-medium uppercase leading-[1em] tracking-[0.02em] text-ink-mute'>
                        {event.dates} · {event.venue}
                    </p>
                </div>
            </div>
        </footer>
    )
}
